/**
 * Tooltip Component
 *
 * A lightweight tooltip that appears on hover/focus. Uses a React portal
 * so it is never clipped by overflow or z-index stacking contexts.
 * Position is calculated via getBoundingClientRect on the trigger element.
 * Animations are handled by framer-motion for smooth enter/exit transitions.
 *
 * @example
 * <Tooltip content="Save changes">
 *   <Button>Save</Button>
 * </Tooltip>
 *
 * <Tooltip content="Delete permanently" side="bottom" variant="error">
 *   <IconButton icon={<TrashIcon />} />
 * </Tooltip>
 *
 * <Tooltip content={<span>Bold <kbd>Ctrl+B</kbd></span>}>
 *   <Button>B</Button>
 * </Tooltip>
 *
 * <Tooltip content="Line one\nLine two" multiline>
 *   <Button>Info</Button>
 * </Tooltip>
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipVariant = 'default' | 'error';

export interface TooltipProps {
  /** Tooltip content - plain text or JSX (e.g. label + keyboard shortcut) */
  content: React.ReactNode;
  /** Which side to show the tooltip */
  side?: TooltipSide;
  /** Color variant */
  variant?: TooltipVariant;
  /** Delay before showing in ms */
  delayMs?: number;
  /** Allow multiline wrapping (pre-line, max-width 300px) */
  multiline?: boolean;
  /** The trigger element */
  children: React.ReactElement;
  /** Additional class on the wrapper */
  className?: string;
  /** When true, the tooltip never shows */
  disabled?: boolean;
}

/** Gap between the trigger element and the tooltip (px) */
const OFFSET = 8;
/** Arrow dimensions (px). Horizontal arrows are wider; side arrows are taller. */
const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 8;
const SIDE_ARROW_WIDTH = 8;
const SIDE_ARROW_HEIGHT = 10;
/** Minimum margin from viewport edge (px) */
const VIEWPORT_MARGIN = 8;

interface Position {
  top: number;
  left: number;
  arrowOffset: number;
}

function getArrowDimensions(side: TooltipSide): { width: number; height: number } {
  return side === 'top' || side === 'bottom'
    ? { width: ARROW_WIDTH, height: ARROW_HEIGHT }
    : { width: SIDE_ARROW_WIDTH, height: SIDE_ARROW_HEIGHT };
}

function computePosition(
  triggerRect: DOMRect,
  tooltipEl: HTMLElement,
  side: TooltipSide,
): Position {
  const tw = tooltipEl.offsetWidth;
  const th = tooltipEl.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const arrow = getArrowDimensions(side);
  const horizontalHalf = arrow.width / 2;
  const verticalHalf = arrow.height / 2;

  let top: number;
  let left: number;

  switch (side) {
    case 'top':
      top = triggerRect.top - th - OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tw / 2;
      break;
    case 'bottom':
      top = triggerRect.bottom + OFFSET;
      left = triggerRect.left + triggerRect.width / 2 - tw / 2;
      break;
    case 'left':
      top = triggerRect.top + triggerRect.height / 2 - th / 2;
      left = triggerRect.left - tw - OFFSET;
      break;
    case 'right':
      top = triggerRect.top + triggerRect.height / 2 - th / 2;
      left = triggerRect.right + OFFSET;
      break;
  }

  const clampedLeft = Math.min(Math.max(left, VIEWPORT_MARGIN), vw - tw - VIEWPORT_MARGIN);
  const clampedTop = Math.min(Math.max(top, VIEWPORT_MARGIN), vh - th - VIEWPORT_MARGIN);

  // Arrow offset: pixel position within the tooltip that points at the trigger center
  let arrowOffset: number;
  if (side === 'top' || side === 'bottom') {
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    arrowOffset = Math.min(
      Math.max(triggerCenterX - clampedLeft, horizontalHalf + 4),
      tw - horizontalHalf - 4,
    );
  } else {
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    arrowOffset = Math.min(
      Math.max(triggerCenterY - clampedTop, verticalHalf + 4),
      th - verticalHalf - 4,
    );
  }

  return { top: clampedTop, left: clampedLeft, arrowOffset };
}

/** Framer-motion animation props based on which side the tooltip is on */
function getMotionProps(side: TooltipSide) {
  const offset = 4;
  const axis = side === 'top' || side === 'bottom' ? 'y' : 'x';
  const dir = side === 'top' || side === 'left' ? 1 : -1;
  return {
    initial: { opacity: 0, [axis]: offset * dir },
    animate: { opacity: 1, [axis]: 0 },
    exit: { opacity: 0, [axis]: offset * dir },
    transition: { duration: 0.12, ease: 'easeOut' as const },
  };
}

export function Tooltip({
  content,
  side = 'top',
  variant = 'default',
  delayMs = 200,
  multiline = false,
  children,
  className,
  disabled = false,
}: TooltipProps) {
  const [visible, setVisible] = React.useState(false);
  const [position, setPosition] = React.useState<Position | null>(null);

  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const show = React.useCallback(() => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, delayMs);
  }, [delayMs, disabled]);

  const showOnFocus = React.useCallback((e: React.FocusEvent) => {
    // Only show tooltip for keyboard-driven focus, not programmatic focus
    // (e.g. when a Dialog returns focus to the trigger element on close)
    if (e.target.matches(':focus-visible')) {
      show();
    }
  }, [show]);

  const hide = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
    setPosition(null);
  }, []);

  // Hide immediately when disabled (e.g. a popover opened over the trigger)
  React.useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  // Cleanup timeouts and rafs on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Recalculate position whenever visibility changes or on scroll/resize
  React.useLayoutEffect(() => {
    if (!visible) return;

    function updatePosition() {
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (!trigger || !tooltip) return;

      const rect = trigger.getBoundingClientRect();
      const pos = computePosition(rect, tooltip, side);
      setPosition(pos);
    }

    // Use rAF so the tooltip DOM is rendered before we measure
    rafRef.current = requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [visible, side]);

  // Build className for tooltip panel
  const tooltipClassName = [
    'tooltip-panel',
    `tooltip-panel-${side}`,
    variant === 'error' && 'tooltip-panel-error',
    multiline && 'tooltip-panel-multiline',
  ]
    .filter(Boolean)
    .join(' ');

  // Only inline styles for positioning
  const positionStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 99999,
    top: position?.top ?? -9999,
    left: position?.left ?? -9999,
    visibility: position ? 'visible' : 'hidden',
    ...(side === 'top' || side === 'bottom'
      ? {
          '--tooltip-arrow-left': `${(position?.arrowOffset ?? 0) - getArrowDimensions(side).width / 2}px`,
        }
      : {
          '--tooltip-arrow-top': `${(position?.arrowOffset ?? 0) - getArrowDimensions(side).height / 2}px`,
        }),
  };

  // Callback ref to assign motion.div's DOM node to our ref
  const setTooltipRef = React.useCallback((node: HTMLDivElement | null) => {
    tooltipRef.current = node;
  }, []);

  const tooltipContent = ReactDOM.createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={setTooltipRef}
          role="tooltip"
          className={tooltipClassName}
          style={positionStyle}
          {...getMotionProps(side)}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>,
    document.fullscreenElement ?? document.body,
  );

  return (
    <>
      <span
        ref={triggerRef}
        className={className ?? 'inline-flex'}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={showOnFocus}
        onBlur={hide}
        onClick={hide}
      >
        {children}
      </span>
      {tooltipContent}
    </>
  );
}
